import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  PaymentObjectType,
  trackPaymentFieldShown,
  trackPaymentMethodSelected,
} from "app/utils/analytics-helpers/academySetup";
import CheckBox from "app/components/inputs/CheckBox";
import ToolTip from "app/routes/Scheduling/ToolTip";
import SwitchToggle from "app/components/OptionButtonGroup/SwitchToggle";
import OnlinePayment from "../../../public/images/online-payment.svg";
import OfflinePayment from "../../../public/images/offline-payment.svg";
import CircleInfo from "../../../public/images/circle-info.svg";
import styles from "./styles.module.scss";

type PropsDef = {
  collectOnline: boolean;
  setCollectOnline: (_v: boolean) => void;
  collectOffline: boolean;
  setCollectOffline: (_v: boolean) => void;
  onlineDefault: boolean;
  setOnlineDefault: (_v: boolean) => void;
  disabled?: boolean;
  forceOnlineDefault?: boolean;
  disableOnline?: boolean;
  disableOffline?: boolean;
  disabledReason?: string;
  showInfoBanner?: boolean;
  /** Academy payment-guard analytics: reports the field shown once and each method turned on. */
  tracking?: { objectType: PaymentObjectType; stripeConnected: boolean };
};

export default function PaymentMethodSelector(props: PropsDef) {
  const {
    collectOnline,
    setCollectOnline,
    collectOffline,
    setCollectOffline,
    onlineDefault,
    setOnlineDefault,
    disabled = false,
    forceOnlineDefault = false,
    disableOnline = false,
    disableOffline = false,
    disabledReason,
    showInfoBanner = false,
    tracking,
  } = props;

  const { t } = useTranslation();

  const shownTracked = useRef(false);
  useEffect(() => {
    if (tracking && !shownTracked.current) {
      shownTracked.current = true;
      trackPaymentFieldShown(tracking.objectType, tracking.stripeConnected);
    }
  }, [tracking]);

  const onCollectOnlineChange = (v: boolean) => {
    if (v && tracking) {
      trackPaymentMethodSelected(
        tracking.objectType,
        "online",
        tracking.stripeConnected,
      );
    }
    setCollectOnline(v);
  };

  const onCollectOfflineChange = (v: boolean) => {
    if (v && tracking) {
      trackPaymentMethodSelected(
        tracking.objectType,
        "offline",
        tracking.stripeConnected,
      );
    }
    setCollectOffline(v);
  };

  // Ensure at least one method is always selected. Once a method has been
  // on, an empty state is the coach turning the last one off, so the
  // fallback to Offline is their choice and is reported like a click; a form
  // that mounts with neither on is still initialising and reports nothing.
  const hadSelection = useRef(collectOffline || collectOnline);
  useEffect(() => {
    if (collectOffline || collectOnline) {
      hadSelection.current = true;
      return;
    }
    if (hadSelection.current) {
      onCollectOfflineChange(true);
    } else {
      setCollectOffline(true);
    }
    // onCollectOfflineChange is recreated each render; the effect keys on the
    // selection, not the handler.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectOffline, collectOnline, setCollectOffline]);

  // Auto-set online as default when forceOnlineDefault is enabled
  useEffect(() => {
    if (forceOnlineDefault && collectOnline) {
      setOnlineDefault(true);
    }
  }, [collectOnline, forceOnlineDefault, setOnlineDefault]);

  // Derived display values for "Default" tag
  const isOnlineDefault =
    (collectOnline && !collectOffline) ||
    (collectOnline && collectOffline && onlineDefault);

  const isOfflineDefault =
    (collectOffline && !collectOnline) ||
    (collectOnline && collectOffline && !onlineDefault);

  return (
    <div className={styles.container}>
      <p className={styles.payment_method_text}>{t("Payment Method")}</p>

      <div className={styles.rowItem}>
        <div className={styles.left}>
          <CheckBox
            className={styles.checkbox_container}
            name="collectOnline"
            checked={collectOnline}
            onChange={onCollectOnlineChange}
            squareCheckIcons
            disabled={disabled || (disableOnline && !collectOnline)}
            label={
              <div className={styles.checkbox_label_container}>
                <img src={OnlinePayment} alt="Online" />
                <span className={styles.labelWithTooltip}>
                  {t("Collect Online")}
                  <ToolTip
                    text={disabledReason || t("You will pay online with Stripe.")}
                    orientation="center"
                    fontSize="18px"
                  />
                </span>
              </div>
            }
          />
        </div>
        {isOnlineDefault && (
          <div className={styles.right}>
            <span className={styles.defaultTag}>{t("Default")}</span>
          </div>
        )}
      </div>

      <div className={styles.rowItem}>
        <div className={styles.left}>
          <CheckBox
            className={styles.checkbox_container}
            name="collectOffline"
            checked={collectOffline}
            onChange={onCollectOfflineChange}
            squareCheckIcons
            disabled={disabled || disableOffline}
            label={
              <div className={styles.checkbox_label_container}>
                <img src={OfflinePayment} alt="Offline" />
                <span className={styles.normalText}>{t("Collect Offline")}</span>
              </div>
            }
          />
        </div>
        {isOfflineDefault && (
          <div className={styles.right}>
            <span className={styles.defaultTag}>{t("Default")}</span>
          </div>
        )}
      </div>

      {showInfoBanner && collectOffline && (
        <div className={styles.infoBanner}>
          <div className={styles.infoBannerHeader}>
            <img src={CircleInfo} alt="Info" />
            <span className={styles.infoBannerTitle}>{t("Offline Payment")}</span>
          </div>
          <p className={styles.infoBannerDescription}>
            {t("Athletes will not be able to purchase Offline products. Assign Athletes to Offline products through the Manage function, then Mark as Paid on the Customer tab.")}
          </p>
        </div>
      )}

      {collectOnline && !forceOnlineDefault && (
        <div className={styles.toggleRow}>
          <SwitchToggle
            checked={collectOnline && !collectOffline ? true : onlineDefault}
            disabled={disabled || (collectOnline && !collectOffline)}
            title={t("Set Online as Default")}
            onChange={(v: boolean) => setOnlineDefault(v)}
          />
        </div>
      )}
    </div>
  );
}
